import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Updated User interface to match new backend schema
interface User {
  id: string;
  email: string;
  username: string;
  fullName: string;
  avatar?: string;
  role: string;
  roles?: string[];
  
  // Tenant-level users (regular users)
  tenantId?: string;
  tenantName?: string;
  tenantType?: string;
  
  // Organization context (available for all users)
  organizationId?: string;
  organizationName?: string;
  
  // System user flag (SUPER_ADMIN, ORG_HEAD)
  isSystemUser?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  
  // For ORG_HEAD: selected tenant filter
  selectedTenantId: string | null;
  
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isOrgHead: () => boolean;
  isTenantHead: () => boolean;
  isSuperAdmin: () => boolean;
  setSelectedTenant: (tenantId: string | null) => void;
  getTenantId: () => string | null; // Returns tenantId or selectedTenantId for ORG_HEAD
  initialize: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true, // Start with loading true to prevent flash
      isHydrated: false,
      selectedTenantId: null,

      login: async (user: User, token: string) => {
        // Store in localStorage for API client
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Store tenant info for regular users
        if (user.tenantId) {
          localStorage.setItem('currentTenantId', user.tenantId);
        }
        
        // Store organization info if available
        if (user.organizationId) {
          localStorage.setItem('currentOrgId', user.organizationId);
        }

        // Initialize selectedTenantId based on user type - ALWAYS SET IT
        let selectedTenantId: string;

        if (user.role === 'ORG_HEAD') {
          // For ORG_HEAD, restore previous selection or auto-select first tenant
          const savedTenantId = localStorage.getItem('selectedTenantId');
          
          if (savedTenantId) {
            // Use previously selected tenant
            selectedTenantId = savedTenantId;
          } else {
            // Fetch tenants and auto-select first one synchronously
            try {
              const { apiClient } = await import('@/services/api');
              const response = await apiClient.getTenants();
              const tenants = response.data?.tenants || response.tenants || [];
              
              if (tenants.length > 0) {
                selectedTenantId = tenants[0].id;
                localStorage.setItem('selectedTenantId', tenants[0].id);
              } else {
                // Fallback if no tenants available
                selectedTenantId = 'NO_TENANTS';
              }
              
              // Store tenants for TenantSelector
              localStorage.setItem('orgHeadTenants', JSON.stringify(tenants));
            } catch (error) {
              console.error('Failed to fetch tenants on login:', error);
              // Set fallback tenant ID to prevent null issues
              selectedTenantId = 'TENANT_FETCH_ERROR';
            }
          }
        } else {
          // For regular tenant users, use their tenant ID directly
          selectedTenantId = user.tenantId!;
        }

        // Set the auth state with guaranteed tenant context
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          selectedTenantId, // Always set - never null
        });

        // Background fetch for ORG_HEAD to refresh tenant list if needed
        if (user.role === 'ORG_HEAD' && user.organizationId) {
          // Don't await this - it's for cache refresh only
          import('@/services/api').then(({ apiClient }) => {
            apiClient.getTenants().then((response) => {
              const tenants = response.data?.tenants || response.tenants || [];
              localStorage.setItem('orgHeadTenants', JSON.stringify(tenants));
            }).catch(() => {
              // Silent fail for background refresh
            });
          });
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          selectedTenantId: null,
        });
        
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('currentTenantId');
        localStorage.removeItem('currentOrgId');
        localStorage.removeItem('selectedTenantId');
        localStorage.removeItem('orgHeadTenants');
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData };
          set({ user: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      },

      hasRole: (role: string): boolean => {
        const user = get().user;
        if (!user) return false;
        
        return user.role === role || (user.roles ? user.roles.includes(role) : false);
      },

      hasAnyRole: (roles: string[]) => {
        const user = get().user;
        if (!user) return false;
        
        return roles.some(role => 
          user.role === role || (user.roles && user.roles.includes(role))
        );
      },

      isOrgHead: () => {
        const user = get().user;
        return user?.role === 'ORG_HEAD' || (user?.roles && user.roles.includes('ORG_HEAD')) || false;
      },

      isTenantHead: () => {
        const user = get().user;
        return user?.role === 'TENANT_HEAD' || 
               (user?.roles && user.roles.includes('TENANT_HEAD')) || 
               false;
      },

      isSuperAdmin: () => {
        const user = get().user;
        return user?.role === 'SUPER_ADMIN' || (user?.roles && user.roles.includes('SUPER_ADMIN')) || false;
      },

      setSelectedTenant: (tenantId: string | null) => {
        set({ selectedTenantId: tenantId });
        if (tenantId) {
          localStorage.setItem('selectedTenantId', tenantId);
        } else {
          localStorage.removeItem('selectedTenantId');
        }
      },

      getTenantId: () => {
        const state = get();
        // Always return selectedTenantId (now guaranteed to exist after login)
        // For ORG_HEAD: this is the selected tenant
        // For tenant users: this is their fixed tenant ID
        return state.selectedTenantId;
      },

      initialize: () => {
        // This will be called after hydration to set loading to false
        set({ isLoading: false, isHydrated: true });
        
        // Restore selectedTenantId if it exists
        const savedTenantId = localStorage.getItem('selectedTenantId');
        if (savedTenantId) {
          set({ selectedTenantId: savedTenantId });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        selectedTenantId: state.selectedTenantId,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after the store has been rehydrated from localStorage
        if (state) {
          state.initialize();
        }
      },
    }
  )
);

// Export User type for use in other files
export type { User };